// deps
import { useCallback, useMemo } from "react";

// helpers
import {
    generatePaymentMethodId,
    getPaymentMethods,
    isPaymentGroupEnabled,
    isPaymentMethodSelected,
} from "@payment-ui/helpers/Payment";

// interfaces
import {
    IPreferredPaymentGroup,
    PaymentMethod,
    PaymentMethodMeta,
    PreferredGroupActions,
} from "@payment-ui/interfaces";
import { PreferredRendererActions } from "@payment-ui/components/payment-group/preferred/interfaces";

type OtherPaymentOffersRendererActions = PreferredRendererActions;
type IOtherPaymentOffersGroup = IPreferredPaymentGroup;

type ReturnType = OtherPaymentOffersRendererActions & {
    paymentMethods: PaymentMethod<PaymentMethodMeta>[];
};

export function useOtherPaymentOffersPaymentGroup(props: IOtherPaymentOffersGroup): ReturnType {
    const { paymentActions, getPreferredSpecificActions, paymentData } = props;

    const isGroupEnabled = useMemo((): boolean => isPaymentGroupEnabled(paymentData), [paymentData]);
    const paymentMethods = useMemo((): PaymentMethod<PaymentMethodMeta>[] => getPaymentMethods(props), [props]);

    const getPaymentActions = useCallback(
        (groupName: string): PreferredGroupActions => {
            const otherActions = getPreferredSpecificActions(groupName);

            return { ...paymentActions, ...otherActions };
        },
        [getPreferredSpecificActions, paymentActions],
    );

    const getPaymentMethodProps = useCallback(
        (paymentMethod: PaymentMethod<PaymentMethodMeta>) => {
            const id = generatePaymentMethodId(paymentMethod);

            return {
                id,
                key: id,
                isPaymentGroupEnabled: isGroupEnabled,
                selected: isPaymentMethodSelected(props.selectedPaymentId, id),
            };
        },
        [isGroupEnabled, props.selectedPaymentId],
    );

    return {
        getPaymentActions,
        getPaymentMethodProps,
        paymentMethods,
    };
}
